<template>
  <div
    class="@container"
    :class="[
      `py-4 px-8 lg:py-8 flex relative rounded-lg text-neutral-950 hover:text-white dark:text-white group`,
      `before:content-[''] before:absolute before:inset-0 before:bg-white before:shadow-lg dark:before:bg-neutral-900 before:z-[2] before:rounded-lg before:transition-all before:duration-300 before:ease-in-out`,
      `after:content-[''] after:absolute after:w-full after:rounded-lg after:h-full after:top-0 after:left-0 after:z-[1] after:bg-cardinal after:transition-all after:duration-300 after:ease-in-out after:translate-x-3 after:translate-y-3`,
      `hover:after:translate-x-0 hover:after:translate-y-0 hover:before:bg-neutral-950 hover:before:duration-50 hover:after:duration-50 `,
    ]"
  >
    <div class="flex flex-col w-full @sm:flex-row gap-8 relative z-[3]">
      <div
        v-if="mediaWithoutVideos?.length"
        class="relative shrink-0 w-48 @sm:w-36 mx-auto @sm:mx-0"
      >
        <!-- Image Carousel -->
        <div class="relative w-48 @sm:w-36 h-[20rem] @sm:h-[11.25rem]">
          <a
            v-for="(media, index) in tweet.attachments.media"
            :key="media.url"
            :href="media.url"
            target="_blank"
            class="absolute inset-0 transition-opacity duration-300"
            :class="index === currentImageIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'"
            title="View on X"
          >
            <img
              class="w-full h-full object-cover object-top rounded-lg"
              :src="media.url"
              :alt="media.alt_text"
            />
          </a>

          <!-- Navigation Arrows -->
          <template v-if="tweet.attachments.media.length > 1">
            <button
              @click.prevent="previousImage"
              class="absolute -left-16 @sm:-left-7 top-1/2 -translate-y-1/2 z-10 hover:text-white dark:text-white rounded-full w-16 h-16 @sm:w-8 @sm:h-8 flex items-center justify-center transition-colors"
              aria-label="Previous image"
            >
              <i class="fas fa-chevron-left text-2xl @sm:text-sm"></i>
            </button>
            <button
              @click.prevent="nextImage"
              class="absolute -right-16 @sm:-right-7 top-1/2 -translate-y-1/2 z-10 hover:text-white dark:text-white rounded-full w-16 h-16 @sm:w-8 @sm:h-8 flex items-center justify-center transition-colors"
              aria-label="Next image"
            >
              <i class="fas fa-chevron-right text-2xl @sm:text-sm"></i>
            </button>

            <!-- Navigation Dots -->
            <div class="absolute -bottom-3 left-1/2 -translate-x-1/2 flex gap-1 z-10">
              <button
                v-for="(_, index) in tweet.attachments.media"
                :key="index"
                @click.prevent="currentImageIndex = index"
                class="w-2 h-2 rounded-full transition-colors"
                :class="
                  index === currentImageIndex
                    ? 'bg-neutral-950 dark:bg-white group-hover:bg-white'
                    : 'bg-neutral-950 dark:bg-white/50 group-hover:bg-white/50 hover:bg-neutral-950/75 dark:hover:bg-white/75'
                "
                :aria-label="'Go to image ' + (index + 1)"
              ></button>
            </div>
          </template>
        </div>
      </div>
      <div class="flex flex-col gap-5 grow">
        <header class="">
          <p
            class="text-sm mb-0 text-neutral-600 group-hover:text-neutral-100 dark:text-neutral-400"
          >
            {{ formatDate(tweet.tweet_created_at) }}
          </p>
        </header>
        <p class="my-2 leading-tight" v-html="processedText" />
        <div class="flex justify-between mt-auto text-sm">
          <p
            class="dark:text-white hover:text-cardinal-500 dark:hover:text-cardinal-500 transition-colors"
          >
            <a href="https://x.com/64Analytics" target="_blank">@64Analytics</a>
          </p>
          <a
            :href="`https://x.com/64Analytics/status/${tweet.tweet_id}`"
            target="_blank"
            class="dark:text-white hover:text-cardinal-500 dark:hover:text-cardinal-500 transition-colors group"
          >
            <span class="group-hover:underline">View on</span>
            <i class="fab fa-x-twitter ml-1"></i>
            <i
              class="far fa-arrow-right ml-1 transition-transform translate-x-0 group-hover:translate-x-1"
            ></i>
          </a>
        </div>
      </div>
    </div>
    <i
      class="fab fa-x-twitter dark:text-white text-neutral-950 text-[196px] opacity-[0.04] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[2]"
    ></i>
  </div>
</template>

<script lang="ts" setup>
import type { Tweet } from '~/types/tweet';
import { processTweetText } from '~/utils/tweet';
import { getRelativeTime } from '~/utils/date';

const props = defineProps<{
  tweet: Tweet;
}>();

const currentImageIndex = ref(0);

const processedText = computed(() => processTweetText(props.tweet.text, props.tweet.entities));

function formatDate(date: string) {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function nextImage() {
  if (!props.tweet.attachments?.media) return;
  currentImageIndex.value = (currentImageIndex.value + 1) % props.tweet.attachments.media.length;
}

function previousImage() {
  if (!props.tweet.attachments?.media) return;
  currentImageIndex.value =
    currentImageIndex.value === 0
      ? props.tweet.attachments.media.length - 1
      : currentImageIndex.value - 1;
}

const mediaWithoutVideos = computed(() => {
  return props.tweet.attachments?.media?.filter(
    (media) => media.type !== 'video' && media.type !== 'animated_gif'
  );
});
</script>
